import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { LeafletModule } from '@asymmetrik/ngx-leaflet';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { featureGroup, Icon, latLng, LatLng, LatLngBounds, Layer, Map as LeafletMap, marker, tileLayer } from 'leaflet';
import 'leaflet-rotatedmarker';
import { combineLatest, of, Subject } from 'rxjs';
import { catchError, debounceTime, switchMap } from 'rxjs/operators';

import { RestStopService } from '../../services/rest-stop.service';
import { LayoutService } from '../../services/layout.service';
import { MapFilterService } from '../../services/filter.service';
import { RestStop } from '../../models/rest-stop';
import { StateSnackBarService } from '../../services/state-snack-bar.service';

const DEFAULT_CENTER = new LatLng(51.1657, 10.4515);
const DEFAULT_ZOOM = 9;
const TILE_LAYER_URL = 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png';
const TILE_LAYER_ATTRIBUTION =
  '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors';

@Component({
  selector: 'app-map',
  standalone: true,
  imports: [LeafletModule, CommonModule, MatIconModule, MatButtonModule],
  templateUrl: './map.component.html',
  styleUrls: ['./map.component.scss'],
})
export class MapComponent implements OnInit, OnDestroy {
  private restStopService = inject(RestStopService);
  private layoutService = inject(LayoutService);
  private filterService = inject(MapFilterService);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private snackBarService = inject(StateSnackBarService);

  private map!: LeafletMap;
  private restStopLayer = featureGroup();
  public layers: Layer[] = [this.restStopLayer];
  public options = {
    layers: [tileLayer(TILE_LAYER_URL, { maxZoom: 17, minZoom: 8, attribution: TILE_LAYER_ATTRIBUTION })],
    zoom: DEFAULT_ZOOM,
    center: DEFAULT_CENTER,
  };

  private boundsChange = new Subject<{ bounds: LatLngBounds; zoom: number }>();
  private displayedMarkers = new Map<number, L.Marker>();
  public userMarker: L.Marker | null = null;
  private watchId: number | null = null;

  ngOnInit(): void {
    combineLatest([this.boundsChange, this.filterService.filters$])
      .pipe(
        debounceTime(200),
        switchMap(([boundsEvent, filters]) => this.restStopService.getRaststaetten(boundsEvent.bounds, filters)),
      )
      .subscribe((stops) => {
        this.updateMarkers(stops);
      });
  }

  ngOnDestroy(): void {
    if (this.watchId !== null) {
      navigator.geolocation.clearWatch(this.watchId);
    }
  }

  public onMapReady(map: LeafletMap): void {
    this.map = map;
    this.setupMapEvents();

    const slug = this.route.snapshot.paramMap.get('slug');
    if (slug) {
      this.restStopService
        .getRestStopBySlug(slug)
        .pipe(
          catchError(() => {
            this.snackBarService.error({ message: `Die Raststätte "${slug}" konnte nicht gefunden werden.` });
            this.router.navigate(['/']);
            return of(null);
          }),
        )
        .subscribe((stop) => {
          if (stop) {
            this.map.setView([stop.lat, stop.lng], 15);
            this.restStopService.notifyMarkerClick(stop);
            this.locateUser(false);
          }
        });
    } else {
      this.locateUser(true);
    }

    setTimeout(() => this.map.fireEvent('moveend'), 500);
  }

  public toggleFilterSidenav(): void {
    this.layoutService.toggleFilters();
  }

  private setupMapEvents(): void {
    this.map.on('dragend zoomend moveend', () => {
      const zoom = this.map.getZoom();
      const bounds = this.map.getBounds();
      const bufferedBounds = bounds.pad(0.5);
      this.boundsChange.next({ bounds: bufferedBounds, zoom });
    });
  }

  private updateMarkers(stops: RestStop[]): void {
    const newStopIds = new Set(stops.map((s) => s.id));

    this.displayedMarkers.forEach((marker, id) => {
      if (!newStopIds.has(id)) {
        this.restStopLayer.removeLayer(marker);

        this.displayedMarkers.delete(id);
      }
    });

    for (const stop of stops) {
      if (!this.displayedMarkers.has(stop.id)) {
        const ratingIconIndex = Math.round(stop.averageRating || 0);

        const iconUrl = `assets/marker_${ratingIconIndex}.png`;

        const markerIcon = new Icon({
          iconUrl: iconUrl,

          iconSize: [64, 64],

          iconAnchor: [32, 64],

          popupAnchor: [5, -60],
        });

        const stopMarker = marker([stop.lat, stop.lng], { icon: markerIcon }).bindPopup(`<b>${stop.name}</b>`);

        stopMarker.on('click', () => this.restStopService.notifyMarkerClick(stop));

        this.restStopLayer.addLayer(stopMarker);

        this.displayedMarkers.set(stop.id, stopMarker);
      }
    }
  }

  private locateUser(shouldCenterMap: boolean): void {
    if (!navigator.geolocation) {
      console.error('Geolokalisierung wird von diesem Browser nicht unterstützt.');
      return;
    }

    if (this.watchId !== null) {
      navigator.geolocation.clearWatch(this.watchId);
    }

    this.watchId = navigator.geolocation.watchPosition(
      (position) => {
        const { latitude, longitude, heading } = position.coords;
        const userLatLng = latLng(latitude, longitude);

        if (!this.userMarker) {
          const userIcon = new Icon({
            iconUrl: 'assets/user-marker.png',
            iconSize: [32, 32],
            iconAnchor: [16, 16],
            rotationAngle: 180,
          });

          this.userMarker = marker(userLatLng, { icon: userIcon });
          this.userMarker.addTo(this.map).bindPopup('<b>Dein Standort</b>');

          if (shouldCenterMap) {
            this.map.setView(userLatLng, 14);
          }
        } else {
          this.userMarker.setLatLng(userLatLng);
        }

        if (heading !== null && this.userMarker) {
          (this.userMarker as any).setRotationAngle(heading);
        }
      },
      (error) => {
        console.error('Fehler bei der Geolokalisierung:', error.message);
      },
      { enableHighAccuracy: true },
    );
  }

  public centerOnUser(): void {
    if (this.userMarker) {
      this.map.setView(this.userMarker.getLatLng(), 14);
    }
  }
}
